import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    getFirestore,
    increment,
    limit,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    startAfter,
    where,
} from './instrumentedFirestore';
import { getFirebaseApp, getSecondaryFirebaseApp } from './app';
import { getAuth, fetchSignInMethodsForEmail } from 'firebase/auth';
import { createSecondaryAuthUser } from './secondaryAuthService';
import {
    COLLECTIONS,
    studentDocPath,
    tenantStudentsRootDocPath,
    tenantClassItemDocPath,
    STUDENT_BADGES_DEFAULT_BRANCH_ID,
} from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function isPlainObject(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

/**
 * Firestore does not allow `undefined` in any field, including nested maps (e.g. address.street,
 * emergencyContact.email). Ant Design forms often set optional keys to undefined; this removes them.
 */
function stripUndefinedDeep(value) {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) return null;
    if (Array.isArray(value)) {
        return value
            .map((item) => stripUndefinedDeep(item))
            .filter((item) => item !== undefined);
    }
    if (isPlainObject(value)) {
        const next = {};
        for (const [k, v] of Object.entries(value)) {
            if (v === undefined) continue;
            const cleaned = stripUndefinedDeep(v);
            if (cleaned !== undefined) {
                next[k] = cleaned;
            }
        }
        return next;
    }
    return value;
}

function normalizeFirestoreValue(value) {
    if (!value) return value;

    // Dayjs / Moment-like values from AntD DatePicker:
    // - dayjs: has toDate()
    // - moment: has toDate()
    if (typeof value?.toDate === 'function') {
        const d = value.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }

    return value;
}

/** Branch doc for `studentBadges/{tenantId}/branches/{branchId}/items/{badgeId}` counts. */
function studentBadgeCountItemRef(tenantId, branchId, badgeId) {
    return doc(db(), COLLECTIONS.STUDENT_BADGES, tenantId, 'branches', branchId, 'items', badgeId);
}

function studentBadgeCountLegacyItemRef(tenantId, badgeId) {
    return doc(db(), COLLECTIONS.STUDENT_BADGES, tenantId, 'items', badgeId);
}

async function resolveBadgeCountBranchId({ tenantId, academicInfo }) {
    if (!academicInfo || typeof academicInfo !== 'object') return STUDENT_BADGES_DEFAULT_BRANCH_ID;
    const direct = academicInfo.badgeBranchId || academicInfo.branchId;
    if (direct) return direct;
    const classId = academicInfo.classId;
    if (classId) {
        const cref = doc(db(), tenantClassItemDocPath(tenantId, classId));
        const cs = await getDoc(cref);
        if (cs.exists()) {
            const bid = cs.data()?.branchId;
            if (bid) return bid;
        }
    }
    return STUDENT_BADGES_DEFAULT_BRANCH_ID;
}

/**
 * Prefer per-campus badge path; use legacy flat `items` only when that doc exists (pre-migration).
 */
async function resolveBadgeCountItemRef({ tenantId, badgeId, academicInfo }) {
    const branchId = await resolveBadgeCountBranchId({ tenantId, academicInfo });
    const modern = studentBadgeCountItemRef(tenantId, branchId, badgeId);
    const legacy = studentBadgeCountLegacyItemRef(tenantId, badgeId);
    const [mSnap, lSnap] = await Promise.all([getDoc(modern), getDoc(legacy)]);
    if (lSnap.exists() && !mSnap.exists()) return legacy;
    return modern;
}

function normalizeStudentProfile(profile) {
    if (!profile || typeof profile !== 'object') return profile;
    return {
        ...profile,
        dateOfBirth: normalizeFirestoreValue(profile.dateOfBirth),
        academicInfo: profile.academicInfo
            ? {
                ...profile.academicInfo,
                admissionDate: normalizeFirestoreValue(profile.academicInfo.admissionDate),
            }
            : profile.academicInfo,
    };
}

export function subscribeStudents({ tenantId, onNext, onError, status }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = collection(db(), 'Students', tenantId, 'students');
    const q = status
        ? query(baseRef, where('status', '==', status), orderBy('createdAt', 'desc'))
        : query(baseRef, orderBy('createdAt', 'desc'));

    return onSnapshot(
        q,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export function subscribeStudentKpis({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const rootRef = doc(db(), tenantStudentsRootDocPath(tenantId));
    return onSnapshot(
        rootRef,
        (snap) => {
            const data = snap.exists() ? snap.data() : {};
            onNext?.({
                totalStudents: data.totalStudents ?? 0,
                activeStudents: data.activeStudents ?? 0,
                inactiveStudents: data.inactiveStudents ?? 0,
            });
        },
        (err) => onError?.(err)
    );
}

export async function fetchStudentKpis({ tenantId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const rootRef = doc(db(), tenantStudentsRootDocPath(tenantId));
    const snap = await getDoc(rootRef);
    const data = snap.exists() ? snap.data() : {};
    return {
        totalStudents: data.totalStudents ?? 0,
        activeStudents: data.activeStudents ?? 0,
        inactiveStudents: data.inactiveStudents ?? 0,
    };
}

/**
 * Creates:
 * - Firebase Auth user (email/password) via secondary auth instance
 * - Firestore student profile at `Students/{tenantId}/students/{studentId}`
 * - Aggregates counters at `Students/{tenantId}`
 *
 * Note: We intentionally DO NOT store the password in Firestore.
 */
export function getSchoolInitials(schoolName) {
    if (!schoolName) return 'SCH';
    const cleanName = schoolName.trim();
    const words = cleanName.split(/[\s-_]+/).filter(Boolean);
    if (words.length === 1) {
        return cleanName.slice(0, 3).toUpperCase();
    }
    return words.map(w => w[0]).join('').toUpperCase();
}

export async function checkStudentIdExists({ tenantId, studentId, excludeStudentId = null }) {
    if (!tenantId || !studentId) return false;
    const baseRef = collection(db(), 'Students', tenantId, 'students');
    const q = query(baseRef, where('studentId', '==', studentId));
    const snap = await getDocs(q);
    if (snap.empty) return false;
    if (excludeStudentId) {
        return snap.docs.some(d => d.id !== excludeStudentId);
    }
    return true;
}

export async function createStudent({
    tenantId,
    email,
    password,
    profile,
}) {
    if (!tenantId) throw new Error('Missing tenantId');

    const tenantRef = doc(db(), COLLECTIONS.TENANTS, tenantId);
    const tenantSnap = await getDoc(tenantRef);
    const tenantData = tenantSnap.exists() ? tenantSnap.data() : {};
    const schoolName = tenantData.schoolName || '';
    const schoolEmail = tenantData.adminEmail || '';

    let finalEmail = email ? email.trim() : '';

    if (!finalEmail) {
        const initials = getSchoolInitials(schoolName);
        const domain = schoolEmail.includes('@') ? schoolEmail.split('@')[1] : 'school.com';
        const cleanFirstName = (profile.firstName || '').trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const cleanLastName = (profile.lastName || '').trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        
        const basePrefix = `${initials}.${cleanFirstName}${cleanLastName}`;
        const cleanDomain = domain.trim().toLowerCase();
        
        let suffix = 0;
        let isUnique = false;
        const auth = getAuth(getSecondaryFirebaseApp());
        
        while (!isUnique) {
            const candidateEmail = suffix === 0 
                ? `${basePrefix}@${cleanDomain}` 
                : `${basePrefix}${suffix}@${cleanDomain}`;
                
            try {
                const methods = await fetchSignInMethodsForEmail(auth, candidateEmail);
                if (methods.length === 0) {
                    const q = query(
                        collection(db(), 'Students', tenantId, 'students'),
                        where('email', '==', candidateEmail)
                    );
                    const snap = await getDocs(q);
                    if (snap.empty) {
                        finalEmail = candidateEmail;
                        isUnique = true;
                    } else {
                        suffix += 1;
                    }
                } else {
                    suffix += 1;
                }
            } catch (err) {
                const q = query(
                    collection(db(), 'Students', tenantId, 'students'),
                    where('email', '==', candidateEmail)
                );
                const snap = await getDocs(q);
                if (snap.empty) {
                    finalEmail = candidateEmail;
                    isUnique = true;
                } else {
                    suffix += 1;
                }
            }
        }
    }

    let studentId = '';
    let authUid = '';

    if (password) {
        const created = await createSecondaryAuthUser({ email: finalEmail, password });
        studentId = created.uid;
        authUid = created.uid;
    } else {
        studentId = doc(collection(db(), 'Students', tenantId, 'students')).id;
        authUid = '';
    }

    const studentRef = doc(db(), studentDocPath(tenantId, studentId));
    const rootRef = doc(db(), tenantStudentsRootDocPath(tenantId));

    const normalizedProfile = stripUndefinedDeep({
        ...normalizeStudentProfile(profile),
        email: finalEmail,
        authUid,
        tenantId,
        status: profile?.status || 'active',
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
    });

    const badgeId = normalizedProfile?.academicInfo?.badgeId || null;
    const badgeCountRef = badgeId
        ? await resolveBadgeCountItemRef({
            tenantId,
            badgeId,
            academicInfo: normalizedProfile.academicInfo,
        })
        : null;

    await runTransaction(db(), async (tx) => {
        const rootSnap = await tx.get(rootRef);
        const prev = rootSnap.exists() ? rootSnap.data() : {};

        tx.set(studentRef, normalizedProfile, { merge: true });

        const nextTotal = (prev.totalStudents ?? 0) + 1;
        const nextActive = (prev.activeStudents ?? 0) + (normalizedProfile.status === 'active' ? 1 : 0);
        const nextInactive = (prev.inactiveStudents ?? 0) + (normalizedProfile.status === 'inactive' ? 1 : 0);

        tx.set(
            rootRef,
            {
                tenantId,
                totalStudents: nextTotal,
                activeStudents: nextActive,
                inactiveStudents: nextInactive,
                updatedAt: serverTimestamp(),
                ...(rootSnap.exists() ? {} : { createdAt: serverTimestamp() }),
            },
            { merge: true }
        );

        if (badgeId && badgeCountRef) {
            tx.set(
                badgeCountRef,
                {
                    assignedStudentCount: increment(1),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        }
    });

    return { studentId, authUid, email: finalEmail };
}

export async function fetchStudentsOnce({ tenantId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = collection(db(), 'Students', tenantId, 'students');
    const q = query(baseRef, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchStudentsPage({ tenantId, pageSize = 10, cursor = null }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = collection(db(), 'Students', tenantId, 'students');
    const countSnap = await getCountFromServer(baseRef);
    const q = cursor
        ? query(baseRef, orderBy('createdAt', 'desc'), startAfter(cursor), limit(pageSize))
        : query(baseRef, orderBy('createdAt', 'desc'), limit(pageSize));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { rows, nextCursor, total: countSnap.data().count || 0 };
}

export async function fetchStudentById({ tenantId, studentId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!studentId) throw new Error('Missing studentId');
    const ref = doc(db(), studentDocPath(tenantId, studentId));
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateStudentProfile({ tenantId, studentId, patch }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!studentId) throw new Error('Missing studentId');
    const studentRef = doc(db(), studentDocPath(tenantId, studentId));
    const preSnap = await getDoc(studentRef);
    const prevData = preSnap.exists() ? preSnap.data() : {};
    const prevAcademic = prevData.academicInfo && typeof prevData.academicInfo === 'object' ? prevData.academicInfo : {};

    const mergedAcademic = patch?.academicInfo
        ? { ...prevAcademic, ...patch.academicInfo }
        : prevAcademic;

    const oldBadge = prevAcademic.badgeId ?? null;
    const newBadge =
        patch?.academicInfo && Object.prototype.hasOwnProperty.call(patch.academicInfo, 'badgeId')
            ? patch.academicInfo.badgeId ?? null
            : oldBadge;

    const patchForNormalize = patch?.academicInfo ? { ...patch, academicInfo: mergedAcademic } : patch;
    const normalized = stripUndefinedDeep({
        ...normalizeStudentProfile(patchForNormalize),
        updatedAt: serverTimestamp(),
    });

    let oldBadgeCountRef = null;
    let newBadgeCountRef = null;
    if (oldBadge !== newBadge) {
        if (oldBadge) {
            oldBadgeCountRef = await resolveBadgeCountItemRef({
                tenantId,
                badgeId: oldBadge,
                academicInfo: prevAcademic,
            });
        }
        if (newBadge) {
            newBadgeCountRef = await resolveBadgeCountItemRef({
                tenantId,
                badgeId: newBadge,
                academicInfo: mergedAcademic,
            });
        }
    }

    await runTransaction(db(), async (tx) => {
        tx.set(studentRef, normalized || {}, { merge: true });
        if (oldBadge !== newBadge) {
            if (oldBadge && oldBadgeCountRef) {
                tx.set(oldBadgeCountRef, { assignedStudentCount: increment(-1), updatedAt: serverTimestamp() }, { merge: true });
            }
            if (newBadge && newBadgeCountRef) {
                tx.set(newBadgeCountRef, { assignedStudentCount: increment(1), updatedAt: serverTimestamp() }, { merge: true });
            }
        }
    });
    return { studentId };
}

